import Reveal from "@/components/base/Reveal";

export default function Features() {
  const features = [
    {
      no: "01",
      title: "Ambient voice",
      desc: "Low-latency conversation that fades into the background of your day — never asking for attention, always ready to listen.",
      meta: "< 120 ms",
      metaLabel: "Response",
      img: "https://readdy.ai/api/search-image?query=Minimalist%20macro%20detail%20of%20a%20matte%20black%20AI%20device%20with%20a%20subtle%20glowing%20warm%20orange%20light%20ring%20around%20its%20central%20lens%2C%20soft%20studio%20lighting%2C%20monochrome%20warm%20gray%20background%2C%20editorial%20industrial%20design%20photography%2C%20refined%20negative%20space%2C%20high%20end%20product%20detail%2C%20quiet%20ambient%20mood&width=1000&height=1200&seq=topbud-feat-01&orientation=portrait",
    },
    {
      no: "02",
      title: "Private memory",
      desc: "Context lives on device — never rented, never sold, always yours. Everything Topbud learns stays within the walls it lives in.",
      meta: "On-device",
      metaLabel: "Storage",
      img: "https://readdy.ai/api/search-image?query=Extreme%20close%20up%20macro%20shot%20of%20smooth%20matte%20black%20curved%20aluminum%20surface%20of%20a%20minimalist%20AI%20device%20with%20a%20single%20soft%20highlight%20arc%2C%20monochrome%20muted%20warm%20paper%20gray%20background%2C%20dramatic%20soft%20studio%20lighting%2C%20editorial%20industrial%20design%20texture%20study&width=1000&height=1200&seq=topbud-feat-02&orientation=portrait",
    },
    {
      no: "03",
      title: "Made to last",
      desc: "One aluminum shell, one glass eye, thirty-six hours of quiet company. Designed to sit still on a shelf for a decade.",
      meta: "36 hrs",
      metaLabel: "Battery",
      img: "https://readdy.ai/api/search-image?query=Minimalist%20still%20life%20of%20a%20small%20matte%20black%20AI%20assistant%20device%20standing%20on%20a%20folded%20warm%20gray%20textile%20with%20subtle%20wrinkles%2C%20side%20light%20from%20left%2C%20long%20soft%20shadow%2C%20muted%20monochrome%20warm%20background%2C%20editorial%20fine%20art%20product%20photography%2C%20refined%20negative%20space&width=1000&height=1200&seq=topbud-feat-03&orientation=portrait",
    },
  ];

  return (
    <section
      id="features"
      className="w-full px-6 md:px-10 py-24 md:py-32 border-t border-background-200"
    >
      {/* Section header row */}
      <Reveal y={30}>
        <div className="flex items-end justify-between mb-12 md:mb-16">
          <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
            <span>02 — Features</span>
          </p>
          <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500 hidden md:block">
            <span>Three, not thirty</span>
          </p>
        </div>
      </Reveal>

      {/* Editorial intro */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-24 mb-20 md:mb-28">
        <Reveal delay={120} y={40}>
          <h2 className="font-heading font-semibold text-[48px] md:text-[68px] leading-[0.95] tracking-[-0.03em] text-foreground-950 max-w-lg">
            What it does <br />
            <span className="italic font-normal text-foreground-700">
              — and nothing more.
            </span>
          </h2>
        </Reveal>
        <Reveal delay={260} y={40}>
          <div className="max-w-md self-end">
            <p className="text-[13px] leading-[1.75] text-foreground-700 text-justify">
              Instead of adding drawers of features, we removed them until only
              three remained. Each one is small enough to describe in a sentence
              and refined enough to live with for a decade.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <span className="h-px w-10 bg-foreground-400" />
              <span className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-600">
                — A shorter list
              </span>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Feature cards — image + editorial text */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {features.map((f, i) => (
          <Reveal key={f.no} delay={i * 160} y={60} duration={1000}>
            <article className="flex flex-col h-full">
              <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-100 border border-background-200">
                <img
                  src={f.img}
                  alt={f.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-[1.03]"
                />
                <span className="absolute left-4 top-4 font-label text-[10px] tracking-[0.22em] uppercase text-background-50">
                  N°{f.no}
                </span>
                <div className="absolute left-4 bottom-4 flex items-baseline gap-2">
                  <span className="font-heading text-[22px] leading-none tracking-[-0.02em] text-background-50">
                    {f.meta}
                  </span>
                  <span className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/80">
                    {f.metaLabel}
                  </span>
                </div>
              </div>

              <div className="mt-6 flex flex-col flex-1">
                <div className="flex items-center gap-3">
                  <span className="font-label text-[11px] tracking-[0.22em] uppercase text-accent-600">
                    {f.no}
                  </span>
                  <span className="h-px flex-1 bg-foreground-300" />
                </div>

                <h3 className="mt-4 font-heading font-semibold text-[26px] md:text-[30px] leading-[1.05] tracking-[-0.02em] text-foreground-950">
                  {f.title}
                </h3>
                <p className="mt-4 text-[13px] leading-[1.75] text-foreground-700 text-justify">
                  {f.desc}
                </p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}